import { liteClient as algoliasearch } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { searchBox, clearRefinements, refinementList, configure, hits, pagination, panel, sortBy, stats, toggleRefinement } from 'instantsearch.js/es/widgets';
import { settings } from './searchSettings.js';
import { attachEventListeners as attachMobileFilterEventListeners } from './algoliaFilterLayover.js';
import { hitTemplateBasic, hitTemplateCard, hitTemplateAgenda, hitTemplateList, hitTemplatePgo, hitTemplateResearch, hitTemplateKidz } from './hitTemplates.js';

import { createDropdown } from './components/dropdown.js';

const {
  VITE_ALGOLIA_APPLICATION_ID,
  VITE_ALGOLIA_SEARCH_API_KEY,
  VITE_ALGOLIA_INDEX_NAME,
} = import.meta.env;

const templates = {
  agenda: hitTemplateAgenda,
  basic: hitTemplateBasic,
  card: hitTemplateCard,
  list: hitTemplateList,
  pgo: hitTemplatePgo,
  research: hitTemplateResearch,
  kidz: hitTemplateKidz,
};

/**
 * Initialize the search functionality.
 * @param {string} targetAudience - Target Audience string name ('patients' | 'members' | 'professionals')
 * @param {Array} tooltips - Tooltips is an array containing tooltip objects { label: string, value: string", tooltip: string } 
 * @param {String} customSlotHTML - Static HTML of content
 * @param {Number} customSlotIndex - Static HTML of content
 * @param {Object} options - Configuration options.
 * @param {number} options.hitsPerPage - Number of hits per page.
 * @param {string} options.selectedTemplate - Template to use for displaying hits.
 * @param {boolean} options.hasSearch - Add search option yes/no.
 * @param {string} options.searchPlaceholder - Add search text to search option.
 * @param {Array} options.sortFilters - Add sorting filters to template.
 * @param {string} options.hitsCssRoot - Define root to add css classes of the hits container.
 * @param {string} options.hitsCssClasses - Add css classes to hits.
 * @param {Array} options.filters - Filters to be applied to the search.
 * @param {Array} options.staticFilterString - Static filters to be applied to the search, but not changeable form the frontend
 * @param {string} options.searchId - Search ID to identify the type of search
 */
const initializeSearch = ({
  targetAudience = "patients",
  tooltips = [],
  hasSearch = true,
  hasClearRefinements = false,
  hitsPerPage = 8,
  selectedTemplate = "basic",
  searchPlaceholder = "Zoeken...",
  filters = [],
  staticFilterString = "",
  sortFilters = [],
  hitsCssRoot = "",
  hitsCssClasses = [],
  customSlotIndex = null,
  customSlotHTML = null,
  overrideIndex = null,
  searchId = null,
}) => {
  const params = new URLSearchParams(window.location.search);
  const medMijParam = params.get('verbindmedmij');

  const searchClient = algoliasearch(
    VITE_ALGOLIA_APPLICATION_ID,
    VITE_ALGOLIA_SEARCH_API_KEY
  );

  const indexName = overrideIndex ? overrideIndex : VITE_ALGOLIA_INDEX_NAME;

  const search = instantsearch({
    indexName,
    searchClient,
    future: {
      preserveSharedStateOnUnmount: true,
    },
    routing: {
      stateMapping: {
        cleanUrlOnDispose: false,
        stateToRoute(uiState) {
          const indexUiState = uiState[indexName];
          const currentParams = new URLSearchParams(window.location.search);
          const verbindmedmij = currentParams.get('verbindmedmij');

          return {
            q: indexUiState.query,
            ...indexUiState.refinementList,
            page: indexUiState.page,
            ...(verbindmedmij && { verbindmedmij }),
          };
        },
        routeToState(routeState) {
          const { verbindmedmij, q, page, ...refinements } = routeState;
          return {
            [indexName]: {
              query: q,
              refinementList: refinements,
              page,
            },
          };
        },
      },
    },
    // custom slots push 2 items to the right, so we need to adjust the hitsPerPage
    searchFunction(helper) {
      if(customSlotHTML){
        // if the page is 0, we need to add 1
        if(helper.state.page === 0) {
          helper.setQueryParameter('hitsPerPage', hitsPerPage + 1);
        // otherwise we keep the original hitsPerPage count
        }else{
          // store and set the page as updating hits per page will reset the page to 0
          const currentPage = helper.getPage();
          helper.setQueryParameter('hitsPerPage', hitsPerPage);
          helper.setPage(currentPage);
        }
      }
      helper.search();
    },
  });

  const targetAudienceString = targetAudience ? `target_audience:"${targetAudience}"` : null ;
  const filterString = staticFilterString && targetAudienceString ? `${staticFilterString} AND ${targetAudienceString}` : staticFilterString ?? targetAudienceString;

  const widgets = [
    configure({
      hitsPerPage,
      filters: filterString
    }),
    hits({
      container: '#hits',
      transformItems(items, { results }) {
        if (customSlotHTML && results.page === 0) {
          const slotIndex = customSlotIndex ?? Math.floor(hitsPerPage / 2);
          const slotContent = {
            isCustomSlot: true,
            content: customSlotHTML,
          };
 
          return [
            ...items.slice(0, slotIndex),
            slotContent,
            ...items.slice(slotIndex),
          ];
        }
 
        return items;
      },
      templates: {
        item: (hit, { html, components}) => {
          return hit?.isCustomSlot ? 
            hit?.content : 
            templates[selectedTemplate](hit, { html, components, targetAudience })
        }, 
      },
      cssClasses: {
        root: hitsCssRoot,
        list: hitsCssClasses,
      },
    }),
  ];

  // Only add pagination if it's not PGO search
  if (searchId !== 'pgoSearch') {
    widgets.push(
      pagination({
        container: '#pagination',
        padding: 2, // How many pages to show before and after the current page
        showFirst: false,
        showLast: false,
        templates: {
          previous: `
          <div class="custom-pagination-prev">
              <span class="sr-only">Ga naar vorige pagina"></span>
              <svg width="16" height="7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 7">
                  <path fill="currentColor" d="m.328 4.517 1.793 2.315a.452.452 0 0 0 .65.057c.182-.17.208-.48.052-.706l-1.69-2.174h14.4c.26 0 .467-.226.467-.508 0-.282-.208-.508-.468-.508H1.134L2.824.819C2.978.62 2.953.31 2.77.113A.373.373 0 0 0 2.485 0c-.13 0-.26.056-.338.17L.354 2.483c-.468.622-.468 1.469-.026 2.033Z"/>
              </svg>
          </div>`,
          next: `
          <div class="custom-pagination-next">
              <span class="sr-only">Ga naar volgende pagina"></span>
              <svg width="16" height="7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 7">
                  <path fill="currentColor" d="M15.672 2.483 13.879.168a.452.452 0 0 0-.65-.057c-.182.17-.208.48-.052.706l1.69 2.174H.466c-.26 0-.467.226-.467.508 0 .282.208.508.468.508h14.398l-1.69 2.174c-.155.198-.13.508.053.706a.373.373 0 0 0 .286.113c.13 0 .26-.056.337-.17l1.794-2.314c.468-.622.468-1.469.026-2.033Z"/>
              </svg>
          </div>`,
          page: (page) => `<div class="custom-pagination-page">${page.page}</div>`,
        },
      })
    );
  }

  // Add search if any are provided
  if (hasSearch) {
    widgets.push(
      searchBox({
        container: '#searchbox',
        placeholder: searchPlaceholder,
      }),
    );
  }

  // Add clearRefinements if any are provided
  if (hasClearRefinements) {
    widgets.push(
      clearRefinements({
        container: '#clear-refinements',
      }),
    );
  }

  const getToolTip = (item) => {
    const { url, label } = item;
    const tooltip = tooltips.filter( tip => { 
      return label === tip.label && 
        url.includes(tip.group) &&
        tip.tooltip.length > 0
    });
    return tooltip.length ? tooltip[0].tooltip : null
  }

  // Add filters dynamically if any are provided
  if (filters && filters.length > 0) {
    filters.forEach(filter => {
      if(filter.label){
        if( filter.type && filter.type === "dropdown" || filter.type === "dropdown__static"){
          // DROPDOWN
          const dropdown = createDropdown(
            refinementList, {
              closeOnChange: () => false,
              cssClasses: { root: `ais-dropdown ${filter.type}` },
              buttonText: filter.label,
              filterDescription: filter.description ?? null,
              limit: 1000,   
              templates: {
                item(item, { html }) {
                  const { label, count, isRefined } = item;
                  const tooltip = getToolTip(item);
                  const replacedLabel = label.replace(/\b\w/g, char => char.toUpperCase()).replace(/\s+/g, '');

                  return html`
                    <label for="checkbox${replacedLabel}" class="relative flex items-center cursor-pointer select-none text-[#111928] text-base font-normal">
                        <input type="checkbox" id="checkbox${replacedLabel}" class="sr-only pgo-checkbox" checked="${isRefined}"/>
                        <div class="flex items-center justify-center w-5 h-5 min-w-5 min-h-5 mr-[0.62rem] border border-stroke dark:border-dark-3 rounded box">
                            <span class="dot min-h-[10px] h-[10px] min-w-[10px] w-[10px] rounded-sm"></span>
                        </div>
                        <span class="nunito-400 text-base text-text-body ${isRefined ? 'nunito-600' : ''}">${label} (${count})</span>
                        ${
                          tooltip
                            ? html`
                              <div class="group relative inline-block ml-1" tabindex="0" aria-describedby="tooltip${replacedLabel}">
                                  <button class="text-base text-text-dark">                           
                                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="12.923" height="12.923" viewBox="0 0 24 24" fill="#110e71">
                                      <path d="M 12 2 C 6.4889971 2 2 6.4889971 2 12 C 2 17.511003 6.4889971 22 12 22 C 17.511003 22 22 17.511003 22 12 C 22 6.4889971 17.511003 2 12 2 z M 12 4 C 16.430123 4 20 7.5698774 20 12 C 20 16.430123 16.430123 20 12 20 C 7.5698774 20 4 16.430123 4 12 C 4 7.5698774 7.5698774 4 12 4 z M 11 7 L 11 9 L 13 9 L 13 7 L 11 7 z M 11 11 L 11 17 L 13 17 L 13 11 L 11 11 z"></path>
                                    </svg>
                                  </button>
                                  <div id="tooltip${replacedLabel}" role="tooltip" class="w-72 absolute bottom-6 xl:bottom-4 xl:-left-1 left-1/2 -translate-x-1/2 xl:translate-x-0 z-20 rounded-xl border border-stroke bg-white py-4 px-4 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus:pointer-events-auto group-focus:opacity-100 drop-shadow-[0px_4px_20px_rgba(0, 0, 0, 0.10)]">
                                    <span class="block mb-1 nunito-600 text-sm text-text-dark">${label}</span>
                                    <span class="text-[0.75rem] text-text-body">${tooltip}</span>
                                  </div>
                              </div>
                            `
                            : ''
                        }
                    </label>
                  `;
                },
              }
            }
          );

          widgets.push(
            dropdown({
              container: `#${filter.container}`,
              attribute: filter.attribute,
              searchable: false,
            }),
          )

        } 
        else if( filter.type && filter.type === "refinementList" ){
          const refinementListWithPanel = panel({
            hidden(options) {
              return options.results.nbHits === 0;
            },
            collapsed: ({ state }) => {
              return state.disjunctiveFacetsRefinements.length !== 0;
            },
            templates: {
              header(options, { html }) {
                if (options.results) {
                  return html`<span>${filter.label}</span>`;
                }
              },
              collapseButtonText(options, { html }) {
                return html`<svg xmlns="http://www.w3.org/2000/svg" width="1em" viewBox="0 0 16 8" fill="none" class="{{ class }}"><path d="M7.71875 7.1875L4.09375 3.53125C3.9375 3.375 3.9375 3.125 4.09375 3L4.3125 2.78125C4.46875 2.625 4.6875 2.625 4.84375 2.78125L8 5.96875L11.125 2.78125C11.2812 2.625 11.5 2.625 11.6562 2.78125L11.875 3C12.0312 3.125 12.0312 3.375 11.875 3.53125L8.25 7.21875C8.09375 7.34375 7.875 7.34375 7.71875 7.1875Z" fill="currentColor"/></svg>`;
              },
            },
          })(refinementList);

          widgets.push(
            refinementListWithPanel({
              container: `#${filter.container}`,
              attribute: filter.attribute,
              templates: {
                item(item, { html }) {
                  const { label, count, isRefined } = item;
                  const tooltip = getToolTip(item);
                  const replacedLabel = label.replace(/\b\w/g, char => char.toUpperCase()).replace(/\s+/g, '');

                  return html`
                     <label for="checkbox${replacedLabel}" class="relative flex items-center cursor-pointer select-none text-[#111928] text-base font-normal">
                        <input type="checkbox" id="checkbox${replacedLabel}" class="sr-only pgo-checkbox" checked="${isRefined}"/>
                        <div class="flex items-center justify-center w-5 h-5 min-w-5 min-h-5 mr-[0.62rem] border border-stroke dark:border-dark-3 rounded box">
                            <span class="dot min-h-[10px] h-[10px] min-w-[10px] w-[10px] rounded-sm"></span>
                        </div>
                        <span class="nunito-400 text-base text-text-body ${isRefined ? 'nunito-600' : ''}">${label} (${count})</span>
                        ${
                          tooltip
                            ? html`
                              <div class="group relative inline-block ml-1" tabindex="0" aria-describedby="tooltip${replacedLabel}">
                                  <button class="text-base text-text-dark">                           
                                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="12.923" height="12.923" viewBox="0 0 24 24" fill="#110e71">
                                      <path d="M 12 2 C 6.4889971 2 2 6.4889971 2 12 C 2 17.511003 6.4889971 22 12 22 C 17.511003 22 22 17.511003 22 12 C 22 6.4889971 17.511003 2 12 2 z M 12 4 C 16.430123 4 20 7.5698774 20 12 C 20 16.430123 16.430123 20 12 20 C 7.5698774 20 4 16.430123 4 12 C 4 7.5698774 7.5698774 4 12 4 z M 11 7 L 11 9 L 13 9 L 13 7 L 11 7 z M 11 11 L 11 17 L 13 17 L 13 11 L 11 11 z"></path>
                                    </svg>
                                  </button>
                                  <div id="tooltip${replacedLabel}" role="tooltip" class="w-72 absolute bottom-6 xl:bottom-4 xl:-left-1 left-1/2 -translate-x-1/2 xl:translate-x-0 z-20 rounded-xl border border-stroke bg-white py-4 px-4 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus:pointer-events-auto group-focus:opacity-100 drop-shadow-[0px_4px_20px_rgba(0, 0, 0, 0.10)]">
                                    <span class="block mb-1 nunito-600 text-sm text-text-dark">${label}</span>
                                    <span class="text-[0.75rem] text-text-body">${tooltip}</span>
                                  </div>
                              </div>
                            `
                            : ''
                        }
                    </label>
                  `;
                },
              }
            })
          );
        }
        else if (filter.type && filter.type === "toggleRefinement") {
          if (
            filter.attribute !== "pgo_medmij_compatible" || 
            medMijParam && filter.attribute === "pgo_medmij_compatible"
          ) {
            widgets.push(
              toggleRefinement({
                container: `#${filter.container}`,
                attribute: filter.attribute,
                on: filter.on ?? true,
                off: filter.off ?? undefined,
                templates: {
                  labelText({ isRefined }, { html }) {
                    return html`
                    <div class="ais-ToggleRefinement-container">
                      <div class="ais-ToggleRefinement-value ais-ToggleRefinement-value${isRefined ? '--checked' : ''}">
                        <span class="sr-only">${isRefined ? filter.label + " is waar" : filter.label + " is niet waar"}</span>
                      </div>
                      <span class="ais-ToggleRefinement-label ${isRefined ? 'font-semibold' : ''}">${filter.label}</span>
                    </div>`;
                  }
                },
                cssClasses: {
                  checkbox: 'sr-only peer',
                  root: 'switch-container',
                },
                transformItems(items) {
                  console.log({
                    items,
                    medMijParam,
                    filter
                  })
                  if (medMijParam && filter.attribute === "pgo_medmij_compatible") {
                    return items.map(item => ({ ...item, isRefined: true }));
                  }
                  return items;
                },
              })
            );
          }
        }
      }
    });

    // show filter stats results
    widgets.push(stats({
      container: '#save-filters-mobile',
      templates: {
        text({ nbHits }, { html }) {
          return html`
            Toon resultaten (${nbHits.toLocaleString()})
          `;
        },
      },
    }));
  }

  // Add sort filters dynamically if any are provided
  if (sortFilters.length > 0) {
    sortFilters.forEach(filter => {
      widgets.push(
        sortBy({
          container: `#${filter.container}`,
          items: filter.items
        })
      );
    }
  )}

  search.addWidgets(widgets);
  search.start();  

  if (medMijParam) {
    const currentState = search.helper.state;
    const updatedState = currentState.addDisjunctiveFacetRefinement('pgo_medmij_compatible', 'true');
    search.helper.setState(updatedState).search();
  }
};

const prepareAlgoliaSearch = async () => {
  const searchPage = document.querySelector("#algolia-search");

  if (!searchPage) return;

  const { 
    searchId, 
    targetAudience, 
    tooltips,
    customSlotHtml,
    customSlotIndex,    
  } = searchPage.dataset;

  const algoliaSettings = settings?.[searchId]?.() ?? null;

  if (!algoliaSettings) {
    console.error("No Search ID dataset could be found. Cancel Algolia generation.");
    return;
  };

  initializeSearch({ 
    ...algoliaSettings, 
    targetAudience: targetAudience,
    tooltips: tooltips ? JSON.parse(tooltips) : [],
    customSlotHTML: customSlotHtml,
    customSlotIndex,
    searchId: searchId
  });

  const filtersContainer = document.querySelector(".filters-container");
  if (!filtersContainer) return;
  attachMobileFilterEventListeners();

};

// Export the initializeSearch function as default
export default prepareAlgoliaSearch;
